import flatten from 'lodash.flatten';

import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
} from '../../../../../services/cards/constants';
import removeEmpty from '../../../../../util/remove-empty';
import uuidv4 from '../../../../../util/uuid';

/**
 * Returns a flattened list of CardElement from CardTemplates.
 *
 * @param {Array<Object>} cardTemplates The CardTemplates.
 * @returns {*[]|Array}
 */
export const getCardElementsFromCardTemplates = cardTemplates => {
  if (!cardTemplates || cardTemplates?.length === 0) {
    return [];
  }

  return flatten(
    cardTemplates.map(cardTemplate =>
      cardTemplate.card_elements.map(cardElement => ({
        ...cardElement,
        card_template_id: cardTemplate.id,
      })),
    ),
  );
};

/**
 * Returns default configuration state for each CardElement type.
 *
 * @param {Object} element The CardElement.
 * @returns {{configuration: {border: any, corner_radius: any, is_editable: any, rectangle_element_configuration: {color: undefined, opacity: undefined}}, element: *}|{configuration: {border: undefined, corner_radius: undefined, is_editable: undefined}, element: *}|{configuration: {border: any, corner_radius: any, text_element_configuration: {font_color: undefined, font_size: undefined, text: undefined, font_family: undefined, text_align: undefined}, is_editable: any}, element: *}|{configuration: {border: any, corner_radius: any, is_editable: any, photo_element_configuration: {existing_asset_image_id: undefined, asset_image_id: undefined, cropbox: undefined}}, element: *}|{configuration: {border: any, corner_radius: any, is_editable: any, sticker_element_configuration: {system_sticker_id: undefined}}, element: *}}
 */
export const getDefaultCardElementConfigurationState = element => {
  const base = {
    element,
    configuration: {
      border: undefined,
      corner_radius: undefined,
      is_editable: undefined,
    },
  };

  if (element.type === CARD_ELEMENT_TYPE.PHOTO) {
    return {
      ...base,
      configuration: {
        ...base.configuration,
        photo_element_configuration: {
          asset_image_id: undefined,
          existing_asset_image_id: undefined,
          cropbox: undefined,
        },
      },
    };
  }

  if (element.type === CARD_ELEMENT_TYPE.RECTANGLE) {
    return {
      ...base,
      configuration: {
        ...base.configuration,
        rectangle_element_configuration: {
          color: undefined,
          opacity: undefined,
        },
      },
    };
  }

  if (element.type === CARD_ELEMENT_TYPE.STICKER) {
    return {
      ...base,
      configuration: {
        ...base.configuration,
        sticker_element_configuration: {
          system_sticker_id: undefined,
        },
      },
    };
  }

  if (element.type === CARD_ELEMENT_TYPE.TEXT) {
    return {
      ...base,
      configuration: {
        ...base.configuration,
        text_element_configuration: {
          text: undefined,
          font_family: undefined,
          font_size: undefined,
          font_color: undefined,
          text_align: undefined,
        },
      },
    };
  }

  return base;
};

/**
 * Gets the CardElements for configuration (CardElementConfiguration)
 * for the createCardElementConfiguration mutation.
 *
 * @param {Object} cardTemplate The AccountCard.
 * @param {Array<Object>} cardElementConfigurationStates The CardElement configuration states as delivered by the UI.
 * @returns {Array}
 */
export const getConfigurableElementsForCardSide = (
  cardTemplate,
  cardElementConfigurationStates,
) => {
  return cardElementConfigurationStates.filter(
    cardElementConfigurationState => {
      const config = removeEmpty(cardElementConfigurationState.configuration);
      const hasConfig = Object.keys(config).length > 0;

      return (
        hasConfig &&
        cardElementConfigurationState.element.card_template_id ===
          cardTemplate.id
      );
    },
  );
};

/**
 * Maps CardElement configuration states to CardElementConfigurationCreateInput.
 *
 * @param {Object} cardTemplateElementConfiguration The card template element configuration.
 * @returns {{[p: string]: *}}
 */
export const mapElementStateToElementConfigurationInput = cardTemplateElementConfiguration => {
  const { configuration, element } = cardTemplateElementConfiguration;

  return {
    card_element_id: element.id,
    ...removeEmpty(configuration),
  };
};

export const getDefaultCardSideGroups = () => {
  return [
    {
      id: uuidv4(),
      name: 'Front Side',
      side: CARD_SIDE.FRONT,
      card_templates: [],
      asset_image_id: null,
      allowed_card_templates: 1,
    },
    {
      id: uuidv4(),
      name: 'Custom Note',
      side: CARD_SIDE.BACK,
      card_templates: [],
      asset_image_id: null,
      allowed_card_templates: 1,
    },
    {
      id: uuidv4(),
      name: 'Custom Design',
      side: CARD_SIDE.BACK,
      card_templates: [],
      asset_image_id: null,
      allowed_card_templates: 1,
    },
    {
      id: uuidv4(),
      name: 'Photo Collage',
      side: CARD_SIDE.BACK,
      card_templates: [],
      asset_image_id: null,
      allowed_card_templates: 6,
    },
  ];
};
